import React, { useRef, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { acAlert, acSwal } from '../utils/acSwal';
import {
	suggestTargetingFromDocument,
	suggestTargetingFromUrl,
} from '../api/aiSuggestApi';
import { targetingFieldsForPackage } from '../data/acOrderRules';

const DOCUMENT_GUIDE =
	'Last opp stillingsannonsen som Word (.docx) eller PDF. Dette gir deg forslag i feltene under. Du kan justere etterpå før innsending. Klikk "Velg fil" for å laste opp, eller dra dokumentet inn i boksen over. Trykk deretter "Generer forslag".';

const LINK_GUIDE =
	'Lim inn lenke til stillingsannonsen. Vi henter teksten og lager forslag i feltene under. Du kan justere etterpå før innsending. Trykk deretter "Generer forslag".';

function fieldHasValue(name, value) {
	if (name === 'hybridHome') {
		return Boolean(value);
	}
	return String(value || '').trim().length > 0;
}

function isSupportedDocument(file) {
	const name = String(file?.name || '').toLowerCase();
	return name.endsWith('.docx') || name.endsWith('.pdf');
}

function normalizeUrlInput(value) {
	const trimmed = String(value || '').trim();
	if (!trimmed) {
		return '';
	}
	if (/^https?:\/\//i.test(trimmed)) {
		return trimmed;
	}
	if (trimmed.includes('.') && !/\s/.test(trimmed)) {
		return `https://${trimmed}`;
	}
	return trimmed;
}

function isHttpUrl(value) {
	try {
		const parsed = new URL(normalizeUrlInput(value));
		return parsed.protocol === 'http:' || parsed.protocol === 'https:';
	} catch {
		return false;
	}
}

const AiTargetingSuggest = ({
	setValue,
	getValues,
	onInferredChange,
	packageChoice,
}) => {
	const inputRef = useRef(null);
	const urlInputRef = useRef(null);
	const [source, setSource] = useState('document');
	const [file, setFile] = useState(null);
	const [url, setUrl] = useState('');
	const [loading, setLoading] = useState(false);
	const [isDragging, setIsDragging] = useState(false);

	const targetingFields = targetingFieldsForPackage(packageChoice);

	const canGenerate =
		source === 'document' ? Boolean(file) : isHttpUrl(url);

	const acceptFile = (next) => {
		if (!next) {
			return;
		}
		if (!isSupportedDocument(next)) {
			void acAlert(
				'Kun Word (.docx) og PDF støttes. Lagre dokumentet som .docx eller .pdf og prøv igjen.'
			);
			if (inputRef.current) {
				inputRef.current.value = '';
			}
			return;
		}
		setFile(next);
		setSource('document');
	};

	const handleFileChange = (event) => {
		acceptFile(event.target.files?.[0] || null);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		if (loading || source !== 'document') {
			return;
		}
		setIsDragging(true);
	};

	const handleDragLeave = (event) => {
		if (event.currentTarget.contains(event.relatedTarget)) {
			return;
		}
		setIsDragging(false);
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setIsDragging(false);
		if (loading) {
			return;
		}
		acceptFile(event.dataTransfer.files?.[0] || null);
	};

	const applyFields = (fields, inferred) => {
		if (!fields || typeof fields !== 'object') {
			return;
		}
		for (const name of targetingFields) {
			if (!(name in fields)) {
				continue;
			}
			if (name === 'hybridHome') {
				setValue(name, Boolean(fields[name]), {
					shouldDirty: true,
					shouldTouch: true,
				});
				continue;
			}
			if (typeof fields[name] !== 'string') {
				continue;
			}
			setValue(name, fields[name], {
				shouldDirty: true,
				shouldTouch: true,
			});
		}
		if (typeof onInferredChange === 'function') {
			const source =
				inferred && typeof inferred === 'object' ? inferred : {};
			const visibleInferred = {};
			for (const name of targetingFields) {
				if (name in source) {
					visibleInferred[name] = source[name];
				}
			}
			onInferredChange(visibleInferred);
		}
	};

	const handleSuggest = async () => {
		if (!canGenerate || loading) {
			return;
		}

		const hasExisting = targetingFields.some((name) =>
			fieldHasValue(name, getValues(name))
		);
		if (hasExisting) {
			const confirm = await acSwal({
				icon: 'question',
				title: 'Overskrive felt?',
				text: 'Noen målrettingsfelt har allerede innhold. Vil du erstatte dem med AI-forslag?',
				showCancelButton: true,
				confirmButtonText: 'Overskriv',
				cancelButtonText: 'Avbryt',
			});
			if (!confirm.isConfirmed) {
				return;
			}
		}

		setLoading(true);
		try {
			const data =
				source === 'link'
					? await suggestTargetingFromUrl(normalizeUrlInput(url))
					: await suggestTargetingFromDocument(file);
			console.log('[AI suggest] backend-respons', data);
			console.log(
				'[AI suggest] companies (hvor_kandidaten_jobber)',
				data?.fields?.companies
			);
			applyFields(data?.fields, data?.inferred);
			await acSwal({
				icon: 'success',
				title: 'Forslag fylt inn',
				text: 'Du kan redigere feltene før du sender inn bestillingen.',
				confirmButtonText: 'OK',
			});
		} catch (error) {
			const message =
				error.response?.data?.error ||
				error.message ||
				'AI utilgjengelig.';
			await acAlert(message, 'Kunne ikke hente forslag');
		} finally {
			setLoading(false);
		}
	};

	const openFilePicker = (event) => {
		event?.stopPropagation?.();
		if (loading || source !== 'document') {
			return;
		}
		inputRef.current?.click();
	};

	const dropzoneClassName = [
		'ai-targeting-suggest__dropzone',
		source === 'link' ? 'is-link-mode' : '',
		isDragging ? 'is-dragging' : '',
		loading ? 'is-loading' : '',
		source === 'document' && file ? 'has-file' : '',
		source === 'link' && isHttpUrl(url) ? 'has-file' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className='ai-targeting-suggest'>
			<h2>Målretting</h2>
			<div className='ai-targeting-suggest__toolbar'>
				<div
					className='ai-targeting-suggest__switch'
					role='radiogroup'
					aria-label='Kilde til AI-forslag'
				>
					<button
						type='button'
						role='radio'
						aria-checked={source === 'document'}
						className={
							source === 'document'
								? 'ai-targeting-suggest__switch-option is-active'
								: 'ai-targeting-suggest__switch-option'
						}
						onClick={() => setSource('document')}
						disabled={loading}
					>
						Dokument
					</button>
					<button
						type='button'
						role='radio'
						aria-checked={source === 'link'}
						className={
							source === 'link'
								? 'ai-targeting-suggest__switch-option is-active'
								: 'ai-targeting-suggest__switch-option'
						}
						onClick={() => setSource('link')}
						disabled={loading}
					>
						Lenke
					</button>
				</div>
				<button
					type='button'
					className='ai-targeting-suggest__submit'
					onClick={handleSuggest}
					disabled={!canGenerate || loading}
				>
					{loading ? 'Genererer forslag...' : 'Generer forslag'}
				</button>
			</div>
			<div
				className={dropzoneClassName}
				aria-busy={loading}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<input
					ref={inputRef}
					className='ai-targeting-suggest__file-input'
					type='file'
					accept='.docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
					onChange={handleFileChange}
					disabled={loading}
					tabIndex={-1}
				/>
				{loading ? (
					<div className='ai-targeting-suggest__loader'>
						<span
							className='ai-targeting-suggest__spinner'
							aria-hidden='true'
						/>
						<span>
							{source === 'link'
								? 'Henter siden og genererer forslag…'
								: 'Genererer forslag…'}
						</span>
					</div>
				) : source === 'link' ? (
					<div className='ai-targeting-suggest__link'>
						<input
							ref={urlInputRef}
							className='ai-targeting-suggest__url-input'
							type='url'
							inputMode='url'
							autoComplete='url'
							placeholder='https://www.firma.no/stilling'
							value={url}
							disabled={loading}
							onChange={(event) => setUrl(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									event.preventDefault();
									void handleSuggest();
								}
							}}
						/>
					</div>
				) : (
					<div className='ai-targeting-suggest__dropzone-body'>
						<button
							type='button'
							className='ai-targeting-suggest__pick'
							onClick={openFilePicker}
							disabled={loading}
						>
							{file ? 'Bytt fil' : 'Velg fil'}
						</button>
						{file ? (
							<p className='ai-targeting-suggest__dropzone-text'>
								{file.name}
							</p>
						) : null}
					</div>
				)}
			</div>
			<small className='field-description-text'>
				{source === 'link' ? LINK_GUIDE : DOCUMENT_GUIDE}
				<FaExclamationCircle size='28' />
			</small>
		</div>
	);
};

export default AiTargetingSuggest;
